import type { StringifiableRecord } from 'query-string';
import queryString from 'query-string';

import type { PermissionType } from '@/utils/hooks/swr/useUserFBPermissions';
import useAcl from '@/utils/hooks/user/useAcl';

// Hooks
import useRouterQuery from '@/hooks/utils/useRouterQuery';

// Types
import type { SocialProfileNetworkType } from '@/types/socialProfileType';

// Libs
import { aclFeatures } from '@/lib/acl/aclFeatures';
import { PERMISSIONS } from '@/lib/permissions/socialPermissions';
import { OAUTH_NETWORKS } from '@/lib/routes/privateRoutes';

type GetOAuthUrlParams = {
    network: SocialProfileNetworkType;
    query?: StringifiableRecord;
    redirect?: {
        url?: string;
        query?: StringifiableRecord;
    };
    permissions?: PermissionType[];
};

const useNetworkOauthUrls = () => {
    const { hasAcl } = useAcl();
    const { deleteAllQueries } = useRouterQuery();

    const getRedirectUrl = (redirect: GetOAuthUrlParams['redirect']) => {
        const currentUrl = deleteAllQueries();
        return queryString.stringifyUrl(
            {
                url: redirect?.url ?? currentUrl,
                query: redirect?.query
            },
            { arrayFormat: 'bracket' }
        );
    };

    const getOauthUrl = ({ network, query, redirect, permissions = [] }: GetOAuthUrlParams) => {
        const hasAccessToPromotedPost = hasAcl(aclFeatures.analytic.promoted_post.read);

        const redirectUrl = getRedirectUrl(redirect);

        const permissionsToRequest = [
            ...(hasAccessToPromotedPost ? [PERMISSIONS.ADS] : []),
            PERMISSIONS.ANALYTICS,
            PERMISSIONS.SCHEDULER,
            ...permissions
        ];

        return queryString.stringifyUrl(
            {
                url: OAUTH_NETWORKS[network],
                query: {
                    ...query,
                    permissions: permissionsToRequest,
                    redirect_to: redirectUrl
                }
            },
            { arrayFormat: 'bracket' }
        );
    };

    return { getOauthUrl };
};

export default useNetworkOauthUrls;
